import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Menu, ShoppingBag, X } from "lucide-react";
import SparkLogo from "./SparkLogo";
import { useCart } from "../context/CartContext";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Reviews", to: "/#reviews" },
  { label: "Delivery & Returns", to: "/shop#delivery" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-md shadow-lav-100/70" : ""
      }`}
      data-testid="navbar"
    >
      <div className="border-b border-lav-100/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
          <Link to="/" data-testid="nav-brand-link" aria-label="Sparkwear home">
            <SparkLogo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-sm font-semibold text-ink/75 transition-colors duration-200 hover:text-blush-700"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/cart"
              data-testid="bag-counter"
              aria-label={`${count} items in bag — view your bag`}
              className="relative inline-flex items-center justify-center rounded-full border border-lav-100 bg-white p-2.5"
            >
              <ShoppingBag className="h-5 w-5 text-ink" />
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 420, damping: 16 }}
                  className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blush-600 px-1 text-[10px] font-bold text-white"
                >
                  {count}
                </motion.span>
              )}
            </Link>

            <Link
              to="/shop"
              data-testid="nav-shop-now-button"
              className="group hidden items-center gap-2 rounded-full bg-ink px-6 py-2.5 font-display text-sm font-semibold text-white shadow-md shadow-ink/15 transition-transform duration-300 hover:scale-[1.04] sm:inline-flex"
            >
              Shop Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>

            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              className="inline-flex items-center justify-center rounded-full border border-lav-100 bg-white p-2.5 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="fixed inset-0 top-[64px] z-40 flex flex-col gap-1 overflow-y-auto bg-cream px-6 py-8 lg:hidden"
        >
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl px-4 py-4 font-display text-2xl font-semibold text-ink transition-colors duration-200 hover:bg-blush-50"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/shop"
            data-testid="mobile-shop-now-button"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-lg font-semibold text-white"
          >
            Shop Now <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      )}
    </header>
  );
}
